import {
  gql,
  MutationQueryReducer,
  MutationResultWrapper,
  useApolloClient,
} from '@apollo/client'
import {orderBy, unionBy} from 'lodash-es'
import {useCallback, useMemo} from 'react'
import {useParams} from 'react-router'

import {Chats} from '../../types/graphqlSchema'
import useSessionMerchantUser from '../useSessionMerchantUser'

const useUpdateChatQueries = (chatId: string, chatFields: string) => {
  const {cache} = useApolloClient()
  const channelId = useParams().channelId?.replaceAll('-', '')
  const {merchantUser: {id: sessionMerchantUserId} = {}} = useSessionMerchantUser()
  const updateChatsQuery: ChatsQuery = useCallback(
    (filter: ChatFilter) => (prev, {mutationResult}) => {
      const chat = cache.readFragment<Chats>({
        fragment: gql`fragment PartialChat on chats {${chatFields}}`,
        id: `chats:${chatId}`,
      })
      return {
        chats: orderBy(
          unionBy(
            [{...chat, ...mutationResult.data?.update_chats_by_pk}],
            (prev.chats as Chats[]),
            'id'
          ).filter(
            ({assignedMerchantUser, isArchived}) => ({
              all: !isArchived,
              archived: isArchived,
              mine: (
                assignedMerchantUser?.id === sessionMerchantUserId
              ) && !isArchived,
              unassigned: !assignedMerchantUser && !isArchived,
            }[filter])
          ),
          'lastActivityTimestamp',
          'desc',
        ),
      }
    },
    [cache, chatFields, chatId, sessionMerchantUserId]
  )
  const updateQueries = useMemo(
    () => Object.fromEntries(
      CHAT_FILTERS.map(f => [`chats_${channelId}_${f}`, updateChatsQuery(f)])
    ),
    [channelId, updateChatsQuery]
  )
  return updateQueries
}

const CHAT_FILTERS: ChatFilter[] = ['mine', 'unassigned', 'all', 'archived']

interface ChatsQuery {
  (_: ChatFilter): MutationQueryReducer<MutationResultWrapper<Chats | undefined>>
}

export default useUpdateChatQueries
